import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import { AdminPageHeader, AdminTableSkeleton, DeleteConfirmDialog } from './shared';
import { InviteAdminDialog } from './InviteAdminDialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Trash2, ShieldCheck, Clock, MailPlus, Loader2 } from 'lucide-react';
import { formatDate } from '@/lib/utils';

interface AdminUser {
  user_id: string;
  email: string | null;
  full_name: string | null;
  avatar_url: string | null;
  created_at: string;
  role: string;
  invite_accepted_at: string | null;
}

export function UsersManager() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<AdminUser | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [resendingFor, setResendingFor] = useState<string | null>(null);

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase.rpc('get_admin_users');

      if (error) {
        console.error('Error fetching admin users:', error);
        toast({
          title: 'Error loading users',
          description: error.message,
          variant: 'destructive',
        });
        return;
      }

      setUsers(data || []);
    } catch (err) {
      console.error('Error in fetchUsers:', err);
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleDeleteClick = (adminUser: AdminUser) => {
    setUserToDelete(adminUser);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!userToDelete) return;

    setIsDeleting(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error('Not authenticated');
      }

      const response = await supabase.functions.invoke('remove-admin', {
        body: { user_id: userToDelete.user_id },
      });

      if (response.error) {
        throw new Error(response.error.message || 'Failed to remove admin');
      }

      toast({
        title: 'Admin removed',
        description: `${userToDelete.email} has been removed from the admin team.`,
      });

      setDeleteDialogOpen(false);
      setUserToDelete(null);
      fetchUsers();
    } catch (err) {
      console.error('Error removing admin:', err);
      toast({
        title: 'Error removing admin',
        description: err instanceof Error ? err.message : 'An error occurred',
        variant: 'destructive',
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const getInitials = (name: string | null, email: string | null): string => {
    if (name) {
      return name
        .split(' ')
        .map((n) => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2);
    }
    if (email) {
      return email[0].toUpperCase();
    }
    return '?';
  };

  const isCurrentUser = (userId: string) => user?.id === userId;
  const isPending = (adminUser: AdminUser) => !adminUser.invite_accepted_at;

  const handleResendInvite = async (adminUser: AdminUser) => {
    setResendingFor(adminUser.user_id);
    try {
      const response = await supabase.functions.invoke('resend-invite', {
        body: { user_id: adminUser.user_id },
      });

      if (response.error) {
        throw new Error(response.error.message || 'Failed to resend invite');
      }

      toast({
        title: 'Invitation resent',
        description: `A new invite email has been sent to ${adminUser.email}`,
      });
    } catch (err) {
      console.error('Error resending invite:', err);
      toast({
        title: 'Failed to resend invite',
        description: err instanceof Error ? err.message : 'An error occurred',
        variant: 'destructive',
      });
    } finally {
      setResendingFor(null);
    }
  };

  return (
    <div className="space-y-4">
      <AdminPageHeader 
        title="Users" 
        count={users.length}
        actions={<InviteAdminDialog onSuccess={fetchUsers} />}
      />

      {loading ? (
        <AdminTableSkeleton columns={5} rows={3} />
      ) : users.length === 0 ? (
        <div className="text-center py-12">
          <ShieldCheck className="mx-auto h-12 w-12 text-muted-foreground" />
          <h3 className="mt-4 text-lg font-semibold">No admin users</h3>
          <p className="text-muted-foreground mt-2">
            Invite administrators to manage the platform.
          </p>
        </div>
      ) : (
        <div className="border rounded-lg">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[50px]"></TableHead>
                <TableHead>User</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Joined</TableHead>
                <TableHead className="w-[80px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.map((adminUser) => (
                <TableRow 
                  key={adminUser.user_id}
                  className={isCurrentUser(adminUser.user_id) ? 'bg-muted/50' : ''}
                >
                  <TableCell>
                    <Avatar className="h-8 w-8">
                      {adminUser.avatar_url && (
                        <AvatarImage src={adminUser.avatar_url} alt={adminUser.full_name || ''} />
                      )}
                      <AvatarFallback className="text-xs">
                        {getInitials(adminUser.full_name, adminUser.email)}
                      </AvatarFallback>
                    </Avatar>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-col">
                      <span className="font-medium">
                        {adminUser.full_name || 'No name'}
                        {isCurrentUser(adminUser.user_id) && (
                          <Badge variant="secondary" className="ml-2 text-xs">
                            You
                          </Badge>
                        )}
                      </span>
                      <span className="text-sm text-muted-foreground">
                        {adminUser.email}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell>
                    {isPending(adminUser) ? (
                      <Badge variant="secondary" className="gap-1">
                        <Clock className="h-3 w-3" />
                        Pending
                      </Badge>
                    ) : (
                      <Badge variant="default" className="gap-1">
                        <ShieldCheck className="h-3 w-3" />
                        Active
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1 text-sm text-muted-foreground">
                      <Clock className="h-3 w-3" />
                      {formatDate(adminUser.created_at, 'medium')}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      {isPending(adminUser) && !isCurrentUser(adminUser.user_id) && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleResendInvite(adminUser)}
                          disabled={resendingFor === adminUser.user_id}
                          title="Resend invite email"
                        >
                          {resendingFor === adminUser.user_id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <MailPlus className="h-4 w-4" />
                          )}
                        </Button>
                      )}
                      {!isCurrentUser(adminUser.user_id) ? (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDeleteClick(adminUser)}
                          className="text-destructive hover:text-destructive hover:bg-destructive/10"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      ) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <DeleteConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        onConfirm={handleDeleteConfirm}
        isDeleting={isDeleting}
        itemName={userToDelete?.email || undefined}
        itemType="admin"
        warning="This will permanently delete their account and they will lose all access to the admin dashboard."
      />
    </div>
  );
}
